import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Cancel as CancelIcon,
  CheckCircle as AcceptIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import apiService from '../../services/api';
import { Invoice, InvoiceStatus, Bid, BidStatus, Company, BidWithRelations, ScoreGrade } from '../../types/models';
import ScoreBadge from '../../components/invoices/ScoreBadge';
import ScoreBreakdown from '../../components/invoices/ScoreBreakdown';
import { formatCurrency } from '../../utils/formatters';
import { getImageUrl, getImageDownloadUrl } from '../../utils/imageUtils';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import { formatDateOnly } from '../../utils/formatters';

// Helper function to get chip color based on status
const getStatusChipColor = (status: InvoiceStatus | string) => {
  switch (status) {
    case InvoiceStatus.PENDING:
    case 'pending':
      return 'warning';
    case InvoiceStatus.VERIFIED:
    case 'verified':
      return 'info';
    case InvoiceStatus.SOLD:
    case 'sold':
      return 'success';
    case InvoiceStatus.EXPIRED:
    case 'expired':
      return 'error';
    case InvoiceStatus.REJECTED:
    case 'rejected':
      return 'error';
    case InvoiceStatus.DELISTED:
    case 'delisted':
      return 'default';
    default:
      return 'default';
  }
};



const InvoiceDetail: React.FC = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [issuerCompany, setIssuerCompany] = useState<Company | null>(null);
  const [bids, setBids] = useState<BidWithRelations[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [delistDialogOpen, setDelistDialogOpen] = useState(false);
  const [delistLoading, setDelistLoading] = useState(false);
  const [delistError, setDelistError] = useState<string | null>(null);
  const [acceptBidDialogOpen, setAcceptBidDialogOpen] = useState(false);
  const [selectedBid, setSelectedBid] = useState<BidWithRelations | null>(null);
  const [acceptBidLoading, setAcceptBidLoading] = useState(false);
  const [acceptBidError, setAcceptBidError] = useState<string | null>(null);

  useEffect(() => {
    const fetchInvoiceDetails = async () => {
      if (!invoiceId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Fetch invoice details
        const invoiceData = await apiService.get<Invoice>(`/invoices/${invoiceId}`);
        setInvoice(invoiceData);
        
        // Check if the invoice has a debtor property (from InvoiceWithRelations)
        if ('debtor' in invoiceData && invoiceData.debtor) {
          setCompany(invoiceData.debtor);
        }
        // If no debtor property, try to fetch company by company_id or debtor_id
        else if (invoiceData.company_id) {
          try {
            const companyData = await apiService.get<Company>(`/companies/${invoiceData.company_id}`);
            setCompany(companyData);
          } catch (companyErr) {
            console.error('Error fetching company details:', companyErr);
            // Don't fail the whole page load if company fetch fails
          }
        } else if (invoiceData.debtor_id) {
          try {
            const companyData = await apiService.get<Company>(`/companies/${invoiceData.debtor_id}`);
            setCompany(companyData);
          } catch (companyErr) {
            console.error('Error fetching company details:', companyErr);
            // Don't fail the whole page load if company fetch fails
          }
        }
        
        // Fetch bids if invoice is verified or sold
        if (invoiceData.status === InvoiceStatus.VERIFIED || invoiceData.status === InvoiceStatus.SOLD) {
          try {
            const bidsData = await apiService.get<BidWithRelations[]>(`/invoices/${invoiceId}/bids`);
            setBids(bidsData);
          } catch (bidErr) {
            console.error('Error fetching bids:', bidErr);
            setBids([]);
          }
        } else {
          setBids([]);
        }
      } catch (err: any) {
        console.error('Error fetching invoice details:', err);
        const errorMessage = typeof err.response?.data?.detail === 'object' 
          ? 'Failed to load invoice details. Please try again later.' 
          : err.response?.data?.detail || 'Failed to load invoice details. Please try again later.';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoiceDetails();
  }, [invoiceId, refreshKey]);

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleDelistClick = () => {
    setDelistDialogOpen(true);
    setDelistError(null);
  };

  const handleDelistClose = () => {
    setDelistDialogOpen(false);
  };

  const handleDelistConfirm = async () => {
    if (!invoice) return;
    
    setDelistLoading(true);
    setDelistError(null);
    
    try {
      // Update invoice status to DELISTED
      await apiService.put(`/invoices/${invoice.id}`, {
        status: InvoiceStatus.DELISTED
      });
      
      // Close dialog and refresh data
      setDelistDialogOpen(false);
      handleRefresh();
    } catch (err: any) {
      console.error('Error delisting invoice:', err);
      const errorMessage = typeof err.response?.data?.detail === 'object' 
        ? 'Failed to delist invoice. Please try again.' 
        : err.response?.data?.detail || 'Failed to delist invoice. Please try again.';
      setDelistError(errorMessage);
    } finally {
      setDelistLoading(false);
    }
  };

  const handleAcceptBidClick = (bid: BidWithRelations) => {
    setSelectedBid(bid);
    setAcceptBidDialogOpen(true);
    setAcceptBidError(null);
  };

  const handleAcceptBidClose = () => {
    setAcceptBidDialogOpen(false);
  };

  const handleAcceptBidConfirm = async () => {
    if (!selectedBid || !invoice) return;
    
    setAcceptBidLoading(true);
    setAcceptBidError(null);
    
    try {
      // Accept the bid
      await apiService.put(`/bids/${selectedBid.id}/accept`, {});
      
      // Update invoice status to SOLD
      await apiService.put(`/invoices/${invoice.id}`, {
        status: InvoiceStatus.SOLD
      });
      
      // Close dialog and refresh data
      setAcceptBidDialogOpen(false);
      handleRefresh();
    } catch (err: any) {
      console.error('Error accepting bid:', err);
      const errorMessage = typeof err.response?.data?.detail === 'object' 
        ? 'Failed to accept bid. Please try again.' 
        : err.response?.data?.detail || 'Failed to accept bid. Please try again.';
      setAcceptBidError(errorMessage);
    } finally {
      setAcceptBidLoading(false);
    }
  };

  const handleDownloadImage = (imageUrl: string | undefined, fileName: string) => {
    if (!imageUrl) return;
    
    // Create a temporary anchor element
    const anchor = document.createElement('a');
    anchor.href = imageUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 4 }}>
          {error}
        </Alert>
        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={() => navigate('/invoices')}
          >
            Back to Invoices
          </Button>
        </Box>
      </Container>
    );
  }

  if (!invoice) {
    return (
      <Container>
        <Alert severity="warning" sx={{ mt: 4 }}>
          Invoice not found
        </Alert>
        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={() => navigate('/invoices')}
          >
            Back to Invoices
          </Button>
        </Box>
      </Container>
    );
  }

  const netAmount = invoice.amount - ((invoice.tax_retention / 100) * invoice.tax_amount);

  return (
    <Container>
      <Box sx={{ mt: 3, mb: 4 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link component={RouterLink} to="/dashboard" color="inherit">
            Panel de Control
          </Link>
          <Link component={RouterLink} to="/invoices" color="inherit">
            My Invoices
          </Link>
          <Typography color="textPrimary">Invoice #{invoice.invoice_number}</Typography>
        </Breadcrumbs>
      </Box>

      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h4" component="h1" sx={{ mr: 2 }}>
            Invoice #{invoice.invoice_number}
          </Typography>
          <Chip
            label={invoice.status.toUpperCase()}
            color={getStatusChipColor(invoice.status)}
          />
        </Box>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={() => navigate('/invoices')}
          >
            Back to Invoices
          </Button>
          {invoice.status !== InvoiceStatus.DELISTED && 
           invoice.status !== InvoiceStatus.SOLD && 
           user && 
           invoice.seller_id === user.id && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={handleDelistClick}
              sx={{ ml: 1 }}
            >
              Delist Invoice
            </Button>
          )}
        </Box>
      </Box>

      {invoice.status === InvoiceStatus.REJECTED && (
        <Alert severity="error" sx={{ mb: 4 }}>
          <Typography variant="subtitle1">Rejection Reason:</Typography>
          <Typography variant="body2">{invoice.rejection_reason || 'No reason provided'}</Typography>
        </Alert>
      )}

      <Grid container spacing={4}>
        {/* Invoice Details */}
        <Grid item xs={12} md={8}>
          <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Detalles de la Factura
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Número de Factura
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {invoice.invoice_number}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Estado
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <Chip
                    label={invoice.status.toUpperCase()}
                    color={getStatusChipColor(invoice.status)}
                    size="small"
                  />
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Fecha de Emisión
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formatDateOnly(invoice.issue_date)}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Fecha de Vencimiento
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formatDateOnly(invoice.due_date)}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Monto Total
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formatCurrency(invoice.amount, invoice.currency)}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Retención de Impuestos
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {invoice.tax_retention}%
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Monto de Impuestos
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formatCurrency(invoice.tax_amount, invoice.currency)}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Monto Neto
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formatCurrency(netAmount, invoice.currency)}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Moneda
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {invoice.currency}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Timbrado
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {invoice.timbrado || 'N/A'}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Monto Mínimo
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {invoice.minimum_amount != null ? formatCurrency(invoice.minimum_amount, invoice.currency) : 'N/A'}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Rating
                </Typography>
                {invoice.effective_grade ? (
                  <ScoreBadge
                    grade={invoice.effective_grade as ScoreGrade}
                    score={invoice.effective_score ?? undefined}
                    size="small"
                    overridden={invoice.score_source === 'override'}
                  />
                ) : (
                  <Typography variant="body1" gutterBottom color="text.disabled">N/A</Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Descripción
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {invoice.description || 'No se proporcionó descripción'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {invoice.effective_grade && (
            <Box sx={{ mb: 4 }}>
              <ScoreBreakdown invoiceId={invoice.id} />
            </Box>
          )}

          {/* Debtor Information */}
          <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Información del Comprador
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            {company ? (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Nombre de la Empresa
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {company.name}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    RUC
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {company.tax_id}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Dirección
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {company.address}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Teléfono
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {company.phone}
                  </Typography>
                </Grid>
              </Grid>
            ) : (
              <Typography variant="body1" color="textSecondary">
                Información de la empresa no disponible
              </Typography>
            )}
          </Paper>
          
          {/* Issuer Information */}
          <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Información del Emisor
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {issuerCompany ? (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Nombre de la Empresa
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {issuerCompany.name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    RUC
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {issuerCompany.tax_id}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Dirección
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {issuerCompany.address}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Teléfono
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {issuerCompany.phone}
                  </Typography>
                </Grid>
              </Grid>
            ) : (
              <Typography variant="body1" color="textSecondary">
                Información del emisor no disponible
              </Typography>
            )}
          </Paper>
          
          {/* Bids Section - Only show if invoice is verified or sold */}
          {(invoice.status === InvoiceStatus.VERIFIED || invoice.status === InvoiceStatus.SOLD) && (
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Ofertas
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {bids.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Comprador</TableCell>
                        <TableCell>Monto</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell>Fecha</TableCell>
                        {user && invoice.seller_id === user.id && invoice.status === InvoiceStatus.VERIFIED && (
                          <TableCell align="right">Acciones</TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bids.map((bid) => (
                        <TableRow key={bid.id} hover>
                          <TableCell>{bid.buyer ? bid.buyer.full_name : bid.buyer_id}</TableCell>
                          <TableCell>{formatCurrency(bid.amount, invoice.currency)}</TableCell>
                          <TableCell>
                            <Chip
                              label={bid.status}
                              color={bid.status === BidStatus.ACCEPTED ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{format(new Date(bid.created_at), 'MMM d, yyyy')}</TableCell>
                          {user && invoice.seller_id === user.id && invoice.status === InvoiceStatus.VERIFIED && (
                            <TableCell align="right">
                              {bid.status === BidStatus.ACTIVE && (
                                <Tooltip title="Aceptar Oferta">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => handleAcceptBidClick(bid)}
                                  >
                                    <AcceptIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body1" color="textSecondary">
                  Aún no se han realizado ofertas para esta factura.
                </Typography>
              )}
            </Paper>
          )}
        </Grid>
        
        {/* Invoice Images */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Imágenes de la Factura
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Card sx={{ mb: 3 }}>
              <CardMedia
                component="img"
                image={getImageUrl(invoice.front_image_path)}
                alt="Frente de la Factura"
                sx={{ maxHeight: 300, objectFit: 'contain' }}
              />
              <CardContent sx={{ py: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Frente de la Factura
                  </Typography>
                  <Tooltip title="Descargar Imagen">
                    <IconButton
                      size="small"
                      onClick={() => handleDownloadImage(getImageDownloadUrl(invoice.front_image_path), `invoice-${invoice.id}-front.jpg`)}
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </CardContent>
            </Card>
            
            {invoice.back_image_path && (
              <Card>
                <CardMedia
                  component="img"
                  image={getImageUrl(invoice.back_image_path)}
                  alt="Back of Invoice"
                  sx={{ maxHeight: 300, objectFit: 'contain' }}
                />
                <CardContent sx={{ py: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle2">
                      Back of Invoice
                    </Typography>
                    <Tooltip title="Download Image">
                      <IconButton
                        size="small"
                        onClick={() => handleDownloadImage(getImageDownloadUrl(invoice.back_image_path), `invoice-${invoice.id}-back.jpg`)}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Paper>
        </Grid>
      </Grid>
      <Dialog
        open={delistDialogOpen}
        onClose={handleDelistClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Delist Invoice
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delist this invoice? This action cannot be undone.
          </DialogContentText>
          {delistError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {delistError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDelistClose} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDelistConfirm}
            color="primary"
            autoFocus
            disabled={delistLoading}
          >
            {delistLoading ? (
              <CircularProgress size={24} />
            ) : (
              'Delist'
            )}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Accept Bid Dialog */}
      <Dialog
        open={acceptBidDialogOpen}
        onClose={handleAcceptBidClose}
        aria-labelledby="accept-bid-dialog-title"
        aria-describedby="accept-bid-dialog-description"
      >
        <DialogTitle id="accept-bid-dialog-title">
          Aceptar Oferta
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="accept-bid-dialog-description">
            ¿Estás seguro de que quieres aceptar esta oferta por {selectedBid && formatCurrency(selectedBid.amount, invoice?.currency || 'USD')}? 
            Esto marcará la factura como VENDIDA y no se puede deshacer.
          </DialogContentText>
          {acceptBidError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {acceptBidError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAcceptBidClose} color="primary">
            Cancelar
          </Button>
          <Button
            onClick={handleAcceptBidConfirm}
            color="primary"
            autoFocus
            disabled={acceptBidLoading}
          >
            {acceptBidLoading ? (
              <CircularProgress size={24} />
            ) : (
              'Aceptar Oferta'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default InvoiceDetail;
